import { supabase } from '@/integrations/supabase/client';
import { logger } from '@/utils/logger';

export interface ContentStats {
  totalModules: number;
  comprehensiveModules: number;
  enhancedModules: number;
  shortModules: number;
  avgWordCount: number;
  lastUpdate: string | null;
}

export interface CourseContentStats extends ContentStats {
  courseId: string;
}

class ContentStatsService {
  private cache: ContentStats | null = null;
  private cacheTimestamp: number = 0;
  private cacheDuration: number = 30000; // 30 seconds

  /**
   * Get real-time content statistics from module_content table
   */
  async getContentStats(): Promise<ContentStats> {
    // Return cached data if fresh
    if (this.cache && Date.now() - this.cacheTimestamp < this.cacheDuration) {
      return this.cache;
    }

    try {
      const { data, error } = await supabase
        .from('module_content')
        .select('content_word_count, updated_at');

      if (error) throw error;

      const stats: ContentStats = {
        totalModules: data?.length || 0,
        comprehensiveModules: data?.filter(m => m.content_word_count >= 2000).length || 0,
        enhancedModules: data?.filter(m => m.content_word_count >= 1000 && m.content_word_count < 2000).length || 0,
        shortModules: data?.filter(m => m.content_word_count < 1000).length || 0,
        avgWordCount: data?.length > 0 
          ? Math.round(data.reduce((sum, m) => sum + m.content_word_count, 0) / data.length)
          : 0,
        lastUpdate: data?.length > 0 
          ? data.reduce((latest, m) => m.updated_at > latest ? m.updated_at : latest, data[0].updated_at)
          : null
      };

      this.cache = stats;
      this.cacheTimestamp = Date.now();

      return stats;
    } catch (error) {
      logger.error('Failed to fetch content stats:', error);
      return {
        totalModules: 0,
        comprehensiveModules: 0,
        enhancedModules: 0,
        shortModules: 0,
        avgWordCount: 0,
        lastUpdate: null
      };
    }
  }

  /**
   * Get content statistics by course
   */
  async getStatsByCourse(): Promise<CourseContentStats[]> {
    try {
      const { data, error } = await supabase
        .from('module_content')
        .select('course_id, content_word_count, updated_at');

      if (error) throw error;

      const courseMap = new Map<string, any[]>();
      data?.forEach(module => {
        if (!courseMap.has(module.course_id)) {
          courseMap.set(module.course_id, []);
        }
        courseMap.get(module.course_id)?.push(module);
      });

      const courseStats: CourseContentStats[] = [];
      courseMap.forEach((modules, courseId) => {
        courseStats.push({
          courseId,
          totalModules: modules.length,
          comprehensiveModules: modules.filter(m => m.content_word_count >= 2000).length,
          enhancedModules: modules.filter(m => m.content_word_count >= 1000 && m.content_word_count < 2000).length,
          shortModules: modules.filter(m => m.content_word_count < 1000).length,
          avgWordCount: Math.round(modules.reduce((sum, m) => sum + m.content_word_count, 0) / modules.length),
          lastUpdate: modules.reduce((latest, m) => m.updated_at > latest ? m.updated_at : latest, modules[0].updated_at)
        });
      });

      return courseStats.sort((a, b) => b.comprehensiveModules - a.comprehensiveModules);
    } catch (error) {
      logger.error('Failed to fetch course stats:', error);
      return [];
    }
  }

  /**
   * Subscribe to real-time updates
   */
  subscribeToUpdates(callback: (stats: ContentStats) => void) {
    const channel = supabase
      .channel('content-stats-updates')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'module_content'
        },
        async () => {
          this.cache = null; // Invalidate cache
          const stats = await this.getContentStats();
          callback(stats);
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }

  /**
   * Clear cache manually
   */
  clearCache() {
    this.cache = null;
    this.cacheTimestamp = 0;
  }
}

export const contentStatsService = new ContentStatsService();
